/**
 * Add / edit lander or offer (UUID + label). Edit prefills label via Voluum POST /entity.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useEffect, useState } from 'react';

import { Button } from '@/components/ui/layout/Button';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/layout/Drawer';
import { Input } from '@/components/ui/form/input/Input';
import { validEntityId, entityIdCopy } from '@/features/flow-optimizer/tracker-entities/identity';
import { useFlowTracker, useFlowTrackerName } from '@/features/flow-optimizer/tracker';

import { useVoluumTrackerEntityPrefill } from './hooks';

export interface PathItemIdDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'edit';
  entityLabel: 'lander' | 'offer';
  initialEntityId: string;
  initialDisplayName: string;
  onCommit: (payload: { entityId: string; displayName: string }) => void;
}

const PathItemIdDialog: FC<PathItemIdDialogProps> = ({
  open,
  onOpenChange,
  mode,
  entityLabel,
  initialEntityId,
  initialDisplayName,
  onCommit,
}) => {
  const [entityId, setEntityId] = useState(initialEntityId);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [error, setError] = useState<string | null>(null);

  const { loading: entityLoading, error: entityFetchError } =
    useVoluumTrackerEntityPrefill({
      open,
      mode,
      entityLabel,
      entityId,
      setDisplayName,
    });

  useEffect(() => {
    if (!open) return;
    setEntityId(initialEntityId);
    setDisplayName(initialDisplayName);
    setError(null);
  }, [open, initialEntityId, initialDisplayName]);

  const title = `${mode === 'add' ? 'Add' : 'Edit'} ${entityLabel}`;
  const trackerName = useFlowTrackerName();
  const tracker = useFlowTracker();
  const copy = entityIdCopy(tracker);

  const handleSave = () => {
    const id = entityId.trim();
    if (!validEntityId(tracker, entityLabel, id)) {
      setError(`Enter a valid ${trackerName} ${copy.kind} for this entity.`);
      return;
    }
    onCommit({
      entityId: id,
      displayName: displayName.trim(),
    });
    onOpenChange(false);
  };

  const statusLine =
    entityFetchError ?? (entityLoading ? `Loading entity from ${trackerName}…` : null);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent variant="center" width="w-[min(440px,92vw)]">
        <DrawerHeader className="px-5 py-3">
          <DrawerTitle className="text-base">{title}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="space-y-3 px-5 py-3">
          {statusLine ? (
            <p className="text-xs text-muted-foreground" role="status">
              {statusLine}
            </p>
          ) : null}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-muted-foreground">
              {entityLabel === 'lander' ? 'Lander' : 'Offer'} {copy.label}
            </label>
            <Input
              value={entityId}
              onChange={(e) => {
                setEntityId(e.target.value);
                setError(null);
              }}
              placeholder={copy.example}
              className="font-mono text-xs"
              autoComplete="off"
              aria-invalid={Boolean(error)}
            />
            {error ? (
              <p className="text-xs text-destructive" role="alert">
                {error}
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-muted-foreground">
              Display label (optional)
            </label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={`Shown in TrafficPulse when ${trackerName} omits a name`}
            />
          </div>
        </DrawerBody>
        <DrawerFooter className="px-5 py-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default PathItemIdDialog;
